export type BarcodeSegment = {
  start: number;
  length: number;
};

export type BarcodePattern = {
  game: BarcodeSegment;
  pack: BarcodeSegment;
  ticket: BarcodeSegment;
  minLength: number;
  totalLength: number;
};

const DEFAULT_BARCODE_FORMAT = '0,4|4,7|12,3|26';
const DEFAULT_BARCODE_LENGTH = 26;

const normalizeBarcodeFormat = (format?: string | null): string => {
  const trimmed = format?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_BARCODE_FORMAT;
};

export const parseBarcodeFormat = (format?: string | null): BarcodePattern => {
  const normalized = normalizeBarcodeFormat(format);
  const parts = normalized.split('|').map((segment) => segment.trim());

  if (parts.length < 3 || parts.length > 4) {
    throw new Error(`Invalid barcode_format '${normalized}': expected 3 or 4 segments`);
  }

  const [game, pack, ticket] = parts.slice(0, 3).map((segment) => {
    const [startStr, lengthStr] = segment.split(',').map((v) => v.trim());
    const start = Number(startStr);
    const length = Number(lengthStr);

    if (Number.isNaN(start) || Number.isNaN(length) || length <= 0 || start < 0) {
      throw new Error(`Invalid barcode_format segment '${segment}'`);
    }

    return { start, length };
  });

  const minLength = Math.max(
    game.start + game.length,
    pack.start + pack.length,
    ticket.start + ticket.length
  );

  let totalLength = DEFAULT_BARCODE_LENGTH;
  if (parts.length === 4) {
    const lengthValue = Number(parts[3]);
    if (Number.isNaN(lengthValue) || lengthValue <= 0) {
      throw new Error(`Invalid barcode_format length '${parts[3]}'`);
    }
    totalLength = lengthValue;
  }

  if (totalLength < minLength) {
    throw new Error(`Invalid barcode_format length '${totalLength}': less than minimum ${minLength}`);
  }

  return { game, pack, ticket, minLength, totalLength };
};

export const extractBarcodeParts = (barcode: string, format?: string | null) => {
  if (!/^[0-9]+$/.test(barcode)) return null;

  let pattern: BarcodePattern;
  try {
    pattern = parseBarcodeFormat(format);
  } catch (error) {
    console.error('[barcodeFormat] Invalid barcode_format:', error);
    return null;
  }

  if (barcode.length !== pattern.totalLength) return null;

  const game = barcode.slice(pattern.game.start, pattern.game.start + pattern.game.length);
  const pack = barcode.slice(pattern.pack.start, pattern.pack.start + pattern.pack.length);
  const ticket = barcode.slice(pattern.ticket.start, pattern.ticket.start + pattern.ticket.length);

  if (!/^[0-9]+$/.test(game + pack + ticket)) return null;

  return {
    game,
    pack,
    ticket,
    rest: barcode.slice(pattern.minLength, pattern.totalLength),
    pattern,
  };
};

export const validateBarcodeFormat = (barcode: string, format?: string | null): boolean =>
  !!extractBarcodeParts(barcode, format);

export const padLeftNumeric = (value: string | number, length: number): string =>
  value.toString().padStart(length, '0');

export const buildPackKeyFromBarcode = (barcode: string, format?: string | null): string | null => {
  const parts = extractBarcodeParts(barcode, format);
  if (!parts) return null;
  return `${parts.game}${parts.pack}`;
};

export const buildPackKeyFromPack = (
  pack: { game_number?: string | number; pack_id?: string | number },
  format?: string | null
): string => {
  const pattern = parseBarcodeFormat(format);
  const game = padLeftNumeric(pack.game_number ?? '', pattern.game.length).slice(-pattern.game.length);
  const packId = padLeftNumeric(pack.pack_id ?? '', pattern.pack.length).slice(-pattern.pack.length);
  return `${game}${packId}`;
};

export const extractStartingTicketNumber = (barcode: string, format?: string | null): string | null => {
  const pattern = parseBarcodeFormat(format);
  const endIndex = pattern.ticket.start + pattern.ticket.length;
  if (barcode.length < endIndex) return null;
  return barcode.slice(0, endIndex);
};

export const buildTicketNumberFromPack = (
  pack: { game_number?: string | number; pack_id?: string | number },
  ticketNumberValue: string | number,
  format?: string | null
): string => {
  const pattern = parseBarcodeFormat(format);
  const outputLength = Math.max(
    pattern.game.start + pattern.game.length,
    pattern.pack.start + pattern.pack.length,
    pattern.ticket.start + pattern.ticket.length
  );
  const output = Array(outputLength).fill('0');
  const game = padLeftNumeric(pack.game_number ?? '', pattern.game.length).slice(-pattern.game.length);
  const packId = padLeftNumeric(pack.pack_id ?? '', pattern.pack.length).slice(-pattern.pack.length);
  const ticket = padLeftNumeric(ticketNumberValue, pattern.ticket.length).slice(-pattern.ticket.length);

  output.splice(pattern.game.start, pattern.game.length, ...game.split(''));
  output.splice(pattern.pack.start, pattern.pack.length, ...packId.split(''));
  output.splice(pattern.ticket.start, pattern.ticket.length, ...ticket.split(''));

  return output.join('');
};
